import {useEffect, useRef, useState} from "react";
import {Alert, Button, Checkbox, Col, Divider, Form, message, Modal, Row, Space, Spin, Upload} from "antd";
import {DownloadOutlined, PlusOutlined} from "@ant-design/icons";
import {InputDropdownParameterDto} from "../../dto/InputDropdownParameterDto";
import {
    ALLOWED_IMAGE_TYPES, ALLOWED_IMAGE_TYPES_TEXT,
    COMMON_DROPDOWN,
    DOCUMENT_TYPES,
    PARAMETER_LIST,
    PREVIEW_DOC_TYPES
} from "../../util/Constants";
import {PageStep} from "../../enums/PageStep";
import {DropdownListDto} from "../../dto/DropdownListDto";
import customerService from "../../service/customerService";
import {CustomerWrapperDto} from "../../dto/CustomerWrapperDto";
import ErrorHandler from "../../util/ErrorHandler";
import {
    MSG_ALLOWED_IMAGE_TYPES_ERROR,
    MSG_FAILED_TO_FETCH_ACKNOWLEDGEMENT_DOCUMENT_ERROR,
    MSG_FAILED_TO_FETCH_AGREEMENTS_ERROR,
    MSG_FAILED_TO_PREVIEW_DOCUMENTS_ERROR,
    MSG_FAILED_TO_UPLOAD_DOCUMENTS_ERROR,
    MSG_PLEASE_CONSENT_TO_THE_ABOVE_AGREEMENTS
} from "../../util/Messages";
import documentService from "../../service/documentService";
import {UploadedDocumentDetailDto} from "../../dto/UploadedDocumentDetailDto";
import {DefaultDocumentDto} from "../../dto/DefaultDocumentDto";
import PreviewModalComp from "../common/preview/PreviewModalComp";
import {step_7_inactive_img} from '../../../AppImageConst';
import {toNumber} from "lodash";

interface AcknowledgementProps {
    savedCusDetailFromPreviousStep: CustomerWrapperDto
    getStepperFilledData: (data: CustomerWrapperDto) => void,
    switchStep?: () => void,
    isFromSummaryView: boolean
}

const TERMS_AND_CONDITIONS_DOCUMENT = "TERMS_AND_CONDITIONS_DOCUMENT";
const DECLARATION_DOCUMENT = "DECLARATION_DOCUMENT";
const Acknowledgement = (props: AcknowledgementProps) => {
    const uploadFileSize: number | undefined = process.env.UPLOAD_FILE_SIZE ? toNumber(process.env.UPLOAD_FILE_SIZE) : 4;
    const [form] = Form.useForm();
    const [agreementList, setAgreementList] = useState<DropdownListDto[]>([]);
    const btnDownload = useRef<HTMLAnchorElement>(null)
    const [signatureUploadedList, setSignatureUploadedList] = useState<DefaultDocumentDto[]>()
    const [disableCheckBoxes, setDisableCheckBoxes] = useState<boolean>(false)
    const [showModalComp, setShowModalComp] = useState<boolean>(false)
    const [isSpinnerLoading, setSpinnerLoading] = useState<boolean>(false)
    const [isDocumentUploaded, setDocumentUploaded] = useState<boolean>(false)
    const [documentName, setDocumentName] = useState<string>()
    const [docURL, setDocURL] = useState('');
    const [docType, setDocType] = useState<PREVIEW_DOC_TYPES>(PREVIEW_DOC_TYPES.IMAGE);


    let customerMstDto = props.savedCusDetailFromPreviousStep.customerMstDto;
    useEffect(() => {
        window.scrollTo(0, 0)
        getAgreementList();
    }, [])

    const beforeUpload = (file: any) => {
        setSpinnerLoading(true)
        let indexOf = ALLOWED_IMAGE_TYPES.indexOf(file.type);
        if (indexOf < 0) {
            message.error(MSG_ALLOWED_IMAGE_TYPES_ERROR);
            setSpinnerLoading(false)
            return Upload.LIST_IGNORE;
        }
        const isLt2M = file.size / 1024 / 1024 <= uploadFileSize;
        if (!isLt2M) {
            message.error(`Document must smaller than ${uploadFileSize}MB!`);
            setSpinnerLoading(false)
            return Upload.LIST_IGNORE;
        }

        const formData = new FormData();
        formData.append("files", file);

        formData.append("jobNo", JSON.stringify(customerMstDto.id));
        formData.append("documentTypeId", JSON.stringify(DOCUMENT_TYPES.SIGN));

        documentService.uploadDocument(formData).then(res => {
            if (res.data !== null) {
                let uploadDocumentDetailDto: UploadedDocumentDetailDto = res.data.results.uploadDocumentDetailDtoList[0];
                form.setFieldsValue({
                    signatureUpload: [{
                        uid: uploadDocumentDetailDto.uploadDocumentDetailId.toString(),
                        name: uploadDocumentDetailDto.originalDocumentName,
                        thumbUrl: uploadDocumentDetailDto.thumbB64,
                        url: uploadDocumentDetailDto.documentPath
                    }]
                })
                setSignatureUploadedList(
                    [{
                        uid: uploadDocumentDetailDto.uploadDocumentDetailId.toString(),
                        name: uploadDocumentDetailDto.originalDocumentName,
                        thumbUrl: uploadDocumentDetailDto.thumbB64,
                        url: uploadDocumentDetailDto.documentPath
                    }]
                )
                setSpinnerLoading(false)
                setDocumentUploaded(true)
            } else {
                setSpinnerLoading(false)
                message.error(MSG_FAILED_TO_UPLOAD_DOCUMENTS_ERROR).then();
            }
        }, (reason) => {
            setSpinnerLoading(false)
            ErrorHandler.handleError(reason);
        })
        return false;
    }

    const configs = {
        name: 'file',
        multiple: false,
        maxCount: 1,
        accept: ".jpeg, .jpg, .png",
    };
    const submitStep = (value: any) => {
        let customerWrapperDto = props.savedCusDetailFromPreviousStep;
        let isSave = customerMstDto.pageWiseAgreementIdList?.every((a) => value.agreementList.includes(a));
        if (!isSave || isDocumentUploaded) {
            let pageWiseAgreementIdList = customerMstDto.pageWiseAgreementIdList;
            customerMstDto.pageWiseAgreementIdList = pageWiseAgreementIdList?.concat(value.agreementList)
            customerWrapperDto.recordStatus = "UPDATE";
            customerWrapperDto.isFinalStep = true;
            customerWrapperDto.currentActiveStep = PageStep.STEP_ACKNOWLEDGMENT_DETAIL;
            customerWrapperDto.customerMstDto = customerMstDto;
            props.getStepperFilledData(customerWrapperDto);
        } else {
            if (props.switchStep) props.switchStep()
        }

    }

    const getAgreementList = () => {

        const inputDropdownParameterDto: InputDropdownParameterDto = {
            dropdownDataSourceId: COMMON_DROPDOWN.AGREEMENT,
            findTextValue: "",
            parameterList: [[2], [PageStep.STEP_ACKNOWLEDGMENT_DETAIL], [PARAMETER_LIST.ACTIVE]]

        }
        customerService.getCommonDropdown(inputDropdownParameterDto).then(res => {
            if (res.data !== null) {
                let activeItemList = res.data.results.activeItemList;
                setAgreementList(activeItemList)
                checkAgreements(activeItemList)
            } else {
                message.error(MSG_FAILED_TO_FETCH_AGREEMENTS_ERROR).then();
            }
        }, (reason) => {
            ErrorHandler.handleError(reason);
        })
    }


    const checkAgreements = (dtoList: DropdownListDto[]) => {
        let result = dtoList.every(value => customerMstDto.pageWiseAgreementIdList?.includes(value.value as number));
        setDisableCheckBoxes(result)
        if (result) {
            form.setFieldsValue({agreementList: customerMstDto.pageWiseAgreementIdList})
            loadUploadedDocuments(customerMstDto.id, ["SIGN"])

        } else {
            setSignatureUploadedList(undefined)
        }
    }

    const validateAgreements = (rule: any, value: any, callback: any) => {


        if (undefined == value) {
            callback(MSG_PLEASE_CONSENT_TO_THE_ABOVE_AGREEMENTS)
        } else if (!agreementList.every((a: DropdownListDto) => value.includes(a.value as number))) {
            callback(MSG_PLEASE_CONSENT_TO_THE_ABOVE_AGREEMENTS)
        } else {
            callback()
        }
    };

    const loadUploadedDocuments = (jobNo: number|undefined, identityDocumentTypeCodeList: string[]) => {
        if (undefined != jobNo) {
            setSpinnerLoading(true)
            documentService.getUploadDocumentListByType(jobNo, identityDocumentTypeCodeList).then(res => {
                if (res.data !== null) {
                    setUploadedDocumentList(res.data.results)
                    setSpinnerLoading(false)
                } else {
                    setSpinnerLoading(false)
                    message.error(MSG_FAILED_TO_FETCH_AGREEMENTS_ERROR).then();
                }
            }, (reason) => {
                setSpinnerLoading(false)
                ErrorHandler.handleError(reason);
            })
        }

    };
    const setUploadedDocumentList = (documents: any) => {
        documents.forEach((document: any) => {
            let uploadDocumentDetailDto: UploadedDocumentDetailDto = document.uploadDocumentDetailDto;
            if (undefined != uploadDocumentDetailDto) {
                setSignatureUploadedList([{
                    uid: uploadDocumentDetailDto.uploadDocumentDetailId.toString(),
                    name: uploadDocumentDetailDto.originalDocumentName,
                    thumbUrl: uploadDocumentDetailDto.thumbB64,
                    url: uploadDocumentDetailDto.documentPath
                }])
                form.setFieldsValue({
                    signatureUpload: [{
                        uid: uploadDocumentDetailDto.uploadDocumentDetailId.toString(),
                        name: uploadDocumentDetailDto.originalDocumentName,
                        thumbUrl: uploadDocumentDetailDto.thumbB64,
                        url: uploadDocumentDetailDto.documentPath
                    }]
                })
            } else {
                setSignatureUploadedList(undefined)
            }
        })

    };


    const showPreview = (value: {}) => {
        setSpinnerLoading(true)
        let documentName = ""
        if (value == 3) {
            documentName = DECLARATION_DOCUMENT;
        } else if (value == 4) {
            documentName = TERMS_AND_CONDITIONS_DOCUMENT;
        }
        documentService.fetchDocuments([documentName]).then(res => {
            if (res.data !== null) {
                let result = res.data.results[0];
                setDocURL(result.thumbB64)
                setDocumentName(result.originalDocumentName)
                setShowModalComp(true)
                setDocType(PREVIEW_DOC_TYPES.PDF)

                setSpinnerLoading(false)
            } else {
                setSpinnerLoading(false)
                message.error(MSG_FAILED_TO_FETCH_ACKNOWLEDGEMENT_DOCUMENT_ERROR).then();
            }
        }, (reason) => {
            setSpinnerLoading(false)
            ErrorHandler.handleError(reason);
        })

    }

    const handleCancel = () => {
        setShowModalComp(false)
    }

    const onDownload = () => {
        btnDownload.current?.click();
    };

    const onRemove = (file: any) => {
        if (customerMstDto && customerMstDto.id) {
            setSpinnerLoading(true)
            let uid = undefined != signatureUploadedList ? signatureUploadedList[0].uid : "0";
            documentService.deleteDocument(uid, customerMstDto.id.toString(), PageStep.STEP_ACKNOWLEDGMENT_DETAIL).then(res => {
                console.log(res)
                if (res.data !== null) {
                    setSignatureUploadedList(undefined)
                    setSpinnerLoading(false)
                    // loadUploadedDocuments(customerMstDto.id, ["SIGN"])
                } else {
                    setSpinnerLoading(false)
                    message.error(MSG_FAILED_TO_UPLOAD_DOCUMENTS_ERROR).then();
                }
            }, (reason) => {
                setSpinnerLoading(false)
                ErrorHandler.handleError(reason);
            })
        }
        return true;
    };

    const normFile = (e: { fileList: any; }) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };

    const onPreview = (file: any) => {
        if (signatureUploadedList) {
            setSpinnerLoading(true)
            let doc = signatureUploadedList[0];
            documentService.viewOriginalDocument(doc.uid).then(res => {
                if (res.data !== null) {
                    console.log("res", res.data)
                    setShowModalComp(true);
                    setDocumentName(doc.name || doc.url!.substring(doc.url!.lastIndexOf('/') + 1));
                    setDocURL(res.data.results)
                    setDocType(checkDocumentContentType(res.data.results))
                    setSpinnerLoading(false)
                } else {
                    setSpinnerLoading(false)
                    message.error(MSG_FAILED_TO_PREVIEW_DOCUMENTS_ERROR).then();
                }
            }, (reason) => {
                setSpinnerLoading(false)
                ErrorHandler.handleError(reason);
            })
        }
        return true;
    };

    const checkDocumentContentType = (base64: string): PREVIEW_DOC_TYPES => {

        if (base64.includes("image/png") || base64.includes("image/jpeg") || base64.includes("image/jpg")) {
            return PREVIEW_DOC_TYPES.IMAGE;
        } else {
            return PREVIEW_DOC_TYPES.PDF;
        }

    };
    return (
        <Spin spinning={isSpinnerLoading} tip="Loading" size="large">
            <div>
                <p className='step_card_header'>
                    <div className="icon-container">
                        <img className='header-icon-img' src={step_7_inactive_img}/>
                    </div>
                    Acknowledgment
                </p>
                <Form
                    className='form-input'
                    layout={"vertical"}
                    initialValues={{remember: true}}
                    form={form}
                    onFinish={submitStep}
                    disabled={props.isFromSummaryView}
                >
                    <Row gutter={[32, 0]}>
                        <Col lg={24} md={24} xl={24} sm={24} xs={24}>
                            <Form.Item
                                name={"agreementList"}
                                label=""
                                rules={[{validator: validateAgreements}]}
                            >
                                <Checkbox.Group style={{width: '50%'}}>
                                    {agreementList && (
                                        agreementList.map((data) => {
                                            return (
                                                <Col lg={12} md={12} xl={12} sm={12} xs={24}>
                                                    <Checkbox value={data.value} disabled={disableCheckBoxes}
                                                              style={{paddingBottom: '5'}}>
                                                        <Button type="link" disabled={false}
                                                                onClick={() => showPreview(data.value)} block>
                                                            {data.label}
                                                        </Button>
                                                    </Checkbox>
                                                </Col>
                                            )
                                        })
                                    )}
                                </Checkbox.Group>
                            </Form.Item>
                            <a hidden ref={btnDownload} href={docURL} download={documentName}></a>
                        </Col>
                        <Col lg={12} md={24} xl={12} sm={24} xs={24}>
                            <div>
                                <h4 className='account-create-form-subheader' style={{marginBottom: 12}}>Please Attach
                                    the Photo of Your Signature (Mandatory)</h4>
                                <Alert style={{marginBottom: 10}} className='note-alert'
                                       description={
                                           <>
                                               <b>Note : </b>Place your signature on a blank piece of paper and attach
                                               an image of the signature below.
                                           </>
                                       }
                                       type="warning"
                                />
                                <Form.Item
                                    label=""
                                    name={"signatureUpload"}
                                    getValueFromEvent={normFile}
                                    initialValue={signatureUploadedList && signatureUploadedList?.length > 0 ? [signatureUploadedList] : undefined}
                                    rules={[{
                                        required: true,
                                        message: 'Please upload your signature'
                                    }]}
                                >
                                    <Upload {...configs}
                                            className='upload_comp'
                                            listType="picture"
                                            fileList={signatureUploadedList && signatureUploadedList?.length > 0 ? signatureUploadedList : undefined}
                                            onRemove={(e: any) => onRemove(e)}
                                            beforeUpload={beforeUpload}
                                            onPreview={(e: any) => onPreview(e)}
                                    >
                                        {(undefined != signatureUploadedList ? signatureUploadedList.length : [].length) < 1 &&
                                            <Button>
                                                <div className='upload-btn-div'>
                                                    <div className="upload-icon-div">
                                                        <PlusOutlined
                                                            style={{fontSize: 20, color: '#757575'}}/>
                                                    </div>
                                                    <div className="upload-btn-text-div">
                                                     <span>
                                                      Upload the Signature {ALLOWED_IMAGE_TYPES_TEXT}
                                                     </span>

                                                    </div>
                                                </div>
                                            </Button>
                                        }
                                    </Upload>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                    {
                        !props.isFromSummaryView ?
                            <>
                                <Row>
                                    <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                                        <Divider></Divider>
                                    </Col>
                                </Row>
                                <Space style={{float: 'right'}} className='form_next_btn_col'>
                                    <Form.Item>
                                        <Button className='form_next_btn' htmlType="submit" type="primary">
                                            Next
                                        </Button>
                                    </Form.Item>
                                </Space>
                            </> : ''
                    }

                </Form>
                <Modal
                    className={docType == PREVIEW_DOC_TYPES.PDF ? "modal-custom-bg custom_border" : ""}
                    title={documentName}
                    centered
                    destroyOnClose={true}
                    visible={showModalComp}
                    onCancel={handleCancel}
                    footer={[
                        <Button onClick={onDownload} style={{background: '#FF7400'}} icon={<DownloadOutlined/>}
                                type="primary">
                            Download
                        </Button>
                    ]}
                    width={docType == PREVIEW_DOC_TYPES.PDF ? 800 : 600}
                >
                    <PreviewModalComp docURL={docURL} divHeight={'70vh'}/>
                </Modal>
            </div>
        </Spin>
    )

}
export default Acknowledgement;

